import { forwardRef, type ButtonHTMLAttributes, type ElementType, type ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 rounded-card font-medium transition-colors ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fc-gold ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

// RESTYLE — Web dark-premium identity. Primary = dorado con texto oscuro
// (fc-bg), secondary = superficie oscura con borde, ghost = transparente con
// hover tintado, danger = rojo con texto blanco. Tokens semánticos.
const variants: Record<Variant, string> = {
  primary: 'bg-primary text-fc-bg hover:bg-primary-600 active:bg-primary-700 shadow-sm',
  secondary:
    'border border-neutral-200 bg-neutral-100 text-neutral-900 hover:bg-neutral-200 shadow-sm',
  ghost: 'bg-transparent text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900',
  danger: 'bg-danger text-white hover:bg-danger-600 active:bg-danger-600 shadow-sm',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-base',
  lg: 'h-12 px-6 text-base',
};

type CommonProps = {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  className?: string;
};

type ButtonAsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps> & {
    as?: undefined;
  };

type ButtonAsElement = CommonProps & {
  as: ElementType;
  [key: string]: unknown;
};

export type ButtonProps = ButtonAsButton | ButtonAsElement;

export const Button = forwardRef<HTMLElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className = '', children, ...rest },
  ref,
) {
  const cls = `${base} ${variants[variant as Variant]} ${sizes[size as Size]} ${className}`.trim();
  const asProp = (rest as { as?: ElementType }).as;
  if (asProp) {
    const Component = asProp;
    const { as: _as, ...elementProps } = rest as { as?: ElementType };
    void _as;
    return (
      <Component ref={ref} className={cls} {...elementProps}>
        {children as ReactNode}
      </Component>
    );
  }
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      className={cls}
      {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {children as ReactNode}
    </button>
  );
});
